import { useMemo, useState } from 'react';
import { Card } from '@/shared/ui/atoms';
import { Prose } from '@/features/onboarding/components/Prose';
import { GLOSSARY } from '@/features/onboarding/data/glossary';
import { REPOS, REPO_ORG_URL, type RepoRole } from '@/features/onboarding/data/repos';

const ROLE_LABEL: Record<RepoRole, string> = { backend: '백엔드', frontend: '프런트', ops: '운영·인프라', app: '앱' };
const ROLE_SKIN: Record<RepoRole, string> = {
  backend: 'border-accent-line bg-accent-soft text-[#0a3069]',
  frontend: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  ops: 'border-line bg-canvas text-muted',
  app: 'border-violet-200 bg-violet-50 text-violet-800',
};

/**
 * 용어 + 저장소 지도 — 회의에서 설명 없이 나오는 말과, 그 말이 사는 저장소를 한 화면에 둔다.
 * 둘을 갈라 놓으면 "그 용어가 어느 코드 얘기인지"를 매번 다시 찾게 된다.
 */
export function GlossaryView(): React.JSX.Element {
  const [query, setQuery] = useState('');

  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle === '') return GLOSSARY;
    return GLOSSARY.map((group) => ({
      ...group,
      terms: group.terms.filter((term) => `${term.term} ${term.meaning} ${term.gotcha ?? ''}`.toLowerCase().includes(needle)),
    })).filter((group) => group.terms.length > 0);
  }, [query]);

  return (
    <div className="flex flex-col gap-4">
      <Card title="🗂️ 저장소 지도" hint="무엇을 고치러 어디를 열어야 하는지 정리했습니다" action={{ label: 'GitHub', onClick: () => window.open(REPO_ORG_URL, '_blank') }}>
        <ul className="flex flex-col">
          {REPOS.map((repo) => (
            <li key={repo.name} className="border-b border-line-soft py-2.5 last:border-b-0">
              <div className="flex flex-wrap items-baseline gap-2">
                <a
                  href={`${REPO_ORG_URL}/${repo.name}`}
                  target="_blank"
                  rel="noreferrer"
                  className="font-mono text-[13px] font-semibold text-accent hover:underline"
                >
                  {repo.name}
                </a>
                <span className={`rounded-full border px-1.5 text-[11px] ${ROLE_SKIN[repo.role]}`}>{ROLE_LABEL[repo.role]}</span>
                <span className="text-[11px] text-faint">{repo.stack}</span>
              </div>
              <div className="text-[12.5px] text-muted"><Prose text={repo.summary} /></div>
              {repo.firstTouch && (
                <div className="text-[11.5px] text-faint">📂 언제 열게 되나요? — {repo.firstTouch}</div>
              )}
            </li>
          ))}
        </ul>
      </Card>

      <Card
        title="📖 용어"
        hint="정본은 각 저장소 문서입니다 — 여기와 어긋나면 저장소 쪽이 맞습니다"
        toolbar={
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="용어 검색"
            className="rounded-md border border-line px-2 py-1 text-[12px] outline-none focus:border-accent"
          />
        }
      >
        <div className="flex flex-col gap-3">
          {groups.map((group) => (
            <div key={group.key}>
              <div className="mb-1 text-[11px] font-semibold tracking-wide text-muted uppercase">{group.title}</div>
              <ul className="flex flex-col">
                {group.terms.map((term) => (
                  <li key={term.term} className="border-b border-line-soft py-2 last:border-b-0">
                    <strong className="text-[13px]">{term.term}</strong>
                    <span className="ml-2 text-[12.5px] text-muted"><Prose text={term.meaning} /></span>
                    {term.gotcha && (
                      <div className="mt-0.5 text-[11.5px] text-amber-800">⚠️ 헷갈리는 지점 — <Prose text={term.gotcha} /></div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {groups.length === 0 && <div className="py-6 text-center text-muted">🔍 검색 결과가 없습니다</div>}
        </div>
      </Card>
    </div>
  );
}
